import React from 'react';
import { motion } from 'framer-motion';
import { 
  CalendarDaysIcon, 
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { BookingCard, SessionTypeCard } from '../components/BookingButton';
import SEO from '../components/SEO';
import { organizationStructuredData } from '../data/seo';

const Booking = () => {
  const sessionTypes = [
    {
      type: 'consultation',
      icon: '💬',
      title: 'Free Consultation',
      description: 'Discuss your learning goals and get a personalized learning plan',
      price: 'Free',
      duration: '30 minutes'
    },
    {
      type: 'lesson',
      icon: '📚',
      title: 'Regular Lesson',
      description: 'Structured learning session with hands-on practice and exercises',
      price: '$50-70/hour',
      duration: '60 minutes'
    },
    {
      type: 'project',
      icon: '🛠️',
      title: 'Project Support',
      description: 'Get help with your coding projects, assignments, or portfolio',
      price: '$60/hour',
      duration: '90 minutes'
    },
    {
      type: 'intensive',
      icon: '⚡',
      title: 'Intensive Session',
      description: 'Deep dive into specific topics with extended practice time',
      price: '$80/hour',
      duration: '2-3 hours'
    }
  ];

  const bookingFeatures = [
    'Flexible scheduling to fit your timezone',
    '1-on-1 personalized attention',
    'Screen sharing and collaborative coding',
    'Recorded sessions for review',
    'Follow-up materials and resources',
    'Progress tracking and feedback'
  ];

  const pricingPlans = [
    {
      title: 'Single Session',
      description: 'Perfect for trying out our teaching style',
      price: '$50-70',
      duration: '1 hour',
      features: [
        '1-on-1 personalized lesson',
        'Screen sharing and collaboration',
        'Follow-up materials',
        'Progress assessment'
      ]
    },
    {
      title: 'Package of 5',
      description: 'Great for consistent learning progress',
      price: '$250-300',
      duration: '5 hours',
      features: [
        '5 personalized lessons',
        'Flexible scheduling',
        'Recorded sessions',
        'Priority support',
        'Learning path customization'
      ]
    },
    {
      title: 'Package of 10',
      description: 'Best value for serious learners',
      price: '$450-550',
      duration: '10 hours',
      features: [
        '10 personalized lessons',
        'Flexible scheduling',
        'Recorded sessions',
        'Priority support',
        'Learning path customization',
        'Career guidance',
        'Portfolio review'
      ]
    }
  ];

  const conversionTrust = [
    'No generic lessons - every session is personalized to your goals',
    'Clear milestone tracking so you know exactly what you are improving',
    'Practical coding support you can apply immediately to projects and work',
    'Priority support for package students to maintain fast momentum'
  ];

  const paymentFAQs = [
    {
      question: 'How do payments work after I book?',
      answer: 'After booking your slot, you receive a confirmation and payment instructions to secure the session. This keeps scheduling simple and fast.'
    },
    {
      question: 'Can I pay per session or in packages?',
      answer: 'Yes. You can start with a single session or choose a package for better value and continuity.'
    },
    {
      question: 'What if I need to move my session?',
      answer: 'You can reschedule with at least 24 hours notice. The paid session credit remains valid for your new time.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <SEO
        title="Book Programming Session - Schedule 1-on-1 Mentorship with Duke"
        description="Book your personalized programming session with Duke. Choose from consultation, regular lessons, project support, or intensive sessions. Flexible scheduling worldwide. Start your coding journey today!"
        keywords="book programming session, schedule coding lesson, programming mentor booking, online coding tutor, programming consultation, coding bootcamp session"
        canonical="/booking"
        ogImage="/og-booking.jpg"
        structuredData={organizationStructuredData}
      />
      {/* Header */}
      <section className="bg-gradient-to-r from-indigo-700 to-cyan-600 text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold font-display mb-6">
              Book Your Session and Start Fast
            </h1>
            <p className="text-xl md:text-2xl text-cyan-100 max-w-3xl mx-auto">
              If you are ready to level up your coding skills, reserve your slot now.
              Choose a session type, lock your time, and get a clear action plan.
            </p>
            <p className="mt-5 text-sm uppercase tracking-wide font-semibold text-cyan-100/90">
              Limited weekly capacity to maintain high-quality 1-on-1 support
            </p>
          </motion.div>
        </div>
      </section>

      {/* Calendly Embed */}
      <section className="py-16 bg-white dark:bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold font-display text-gray-900 dark:text-white mb-4">
              Schedule Your Session
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Choose your preferred time slot and reserve your coaching session
            </p>
          </motion.div>

          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-md p-8">
            <div className="bg-gray-100 dark:bg-slate-700 rounded-xl p-8 text-center">
              <CalendarDaysIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Reserve Your Time Slot
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Book your session in Calendly and secure your spot immediately.
              </p>
              <a
                href="https://calendly.com/dukelester4/one-on-one-session"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-cyan-500 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-cyan-600 transition-all duration-200 transform hover:scale-[1.02]"
              >
                <CalendarDaysIcon className="h-5 w-5 mr-2" />
                Book and Reserve Slot
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Session Types */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold font-display text-gray-900 dark:text-white mb-4">
              Session Types
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Choose the session type that best fits your learning needs
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {sessionTypes.map((session, index) => (
              <SessionTypeCard key={session.type} {...session} />
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-16 bg-white dark:bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold font-display text-gray-900 dark:text-white mb-4">
              Pricing Plans
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Choose the plan that fits your goals. Packages offer better value and faster progress.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <BookingCard key={index} {...plan} />
            ))}
          </div>
        </div>
      </section>

      {/* Ready-to-Pay Confidence */}
      <section className="py-16 bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl font-bold font-display mb-4">Ready to Pay and Start?</h2>
            <p className="text-slate-200 text-lg max-w-3xl mx-auto">
              Book first, then secure your session with payment instructions sent right after confirmation.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {conversionTrust.map((item, index) => (
              <div key={index} className="rounded-xl border border-white/15 bg-white/5 p-5">
                <div className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-cyan-300 mr-3 mt-0.5" />
                  <p className="text-slate-100">{item}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center">
            <a
              href="https://calendly.com/dukelester4/one-on-one-session"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-7 py-3 bg-gradient-to-r from-indigo-600 to-cyan-500 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-cyan-600 transition-all duration-200 transform hover:scale-[1.02]"
            >
              <CalendarDaysIcon className="h-5 w-5 mr-2" />
              Book Now - Secure Your Spot
            </a>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold font-display text-gray-900 dark:text-white mb-4">
              What's Included
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Every session comes with these features
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {bookingFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-md hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center">
                  <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                  <span className="text-gray-900 dark:text-white font-medium">
                    {feature}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white dark:bg-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold font-display text-gray-900 dark:text-white mb-4">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                question: "How do I book a session?",
                answer: "Simply click the 'Book Session' button and choose your preferred time slot. You'll receive a confirmation email with meeting details."
              },
              {
                question: "What technology do I need?",
                answer: "You'll need a computer with internet connection, a webcam, and microphone. We'll use Zoom or Google Meet for video calls and screen sharing."
              },
              {
                question: "Can I reschedule or cancel?",
                answer: "Yes, you can reschedule or cancel up to 24 hours before your session. Changes can be made through your booking confirmation email."
              },
              {
                question: "What if I'm a complete beginner?",
                answer: "Perfect! I specialize in teaching beginners. We'll start with the fundamentals and build up at your pace."
              },
              {
                question: "Do you provide materials after the session?",
                answer: "Yes, you'll receive session notes, code examples, and additional resources to practice between sessions."
              },
              ...paymentFAQs
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-50 dark:bg-slate-700 rounded-2xl border border-slate-200 dark:border-slate-600 p-6"
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {faq.question}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {faq.answer}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Booking;
